import React, { useState, useEffect } from 'react';
import axios from 'axios';
const API_BASE_URL = process.env.REACT_APP_API_URL

function IPWhitelist() {
  const [showForm, setShowForm] = useState(false);
  const [showChangeLogs, setShowChangeLogs] = useState(false);
  const [formData, setFormData] = useState({
    ip_address: '',
    description: '',
    purpose: '',
    expires_at: '',
  });
  const [ipList, setIpList] = useState([]);
  const [changeLogs, setChangeLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
  const [filter, setFilter] = useState('');

  useEffect(() => {
    fetchIPList();
  }, [pagination.page, filter]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('accessToken');
    const apiKey = localStorage.getItem('apiKey');
    
    if (token) {
      return { 'Authorization': `Bearer ${token}` };
    } else if (apiKey) {
      return { 'X-API-Key': apiKey };
    }
    return {};
  };

  const fetchIPList = async () => {
    setLoading(true);
    try {
      const params = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (filter) {
        params.is_active = filter === 'active';
      }

      const response = await axios.get(`${API_BASE_URL}/ip-whitelist`, {
        params,
        headers: getAuthHeaders()
      });
      
      setIpList(response.data.data || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Error fetching IP list:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchChangeLogs = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/ip-whitelist/change-logs`, {
        headers: getAuthHeaders()
      });
      setChangeLogs(response.data.data || []);
      setShowChangeLogs(true);
    } catch (error) {
      console.error('Error fetching change logs:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.ip_address.trim()) {
      setMessage({ type: 'error', text: 'IP address is required' });
      return;
    }

    try {
      await axios.post(`${API_BASE_URL}/ip-whitelist`, {
        ip_address: formData.ip_address,
        description: formData.description,
        purpose: formData.purpose,
        expires_at: formData.expires_at || null,
      }, {
        headers: getAuthHeaders()
      });
      
      setMessage({ type: 'success', text: 'IP added to whitelist successfully!' });
      setFormData({ ip_address: '', description: '', purpose: '', expires_at: '' });
      setShowForm(false);
      fetchIPList();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to add IP' 
      });
    }
  };

  const handleRemove = async (id, ip) => {
    if (!window.confirm(`Remove IP ${ip} from whitelist?`)) return;

    try {
      await axios.delete(`${API_BASE_URL}/ip-whitelist/${id}`, {
        headers: getAuthHeaders()
      });
      
      setMessage({ type: 'success', text: 'IP removed from whitelist' });
      fetchIPList();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to remove IP' 
      });
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1>IP Access Control</h1>
        <p>IP 접근 통제 (개인정보보호법 제6조 - 접근 통제)</p>
        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
          <button 
            className="btn btn-primary" 
            onClick={() => {
              setShowForm(!showForm);
              setFormData({ ip_address: '', description: '', purpose: '', expires_at: '' });
            }}
          >
            {showForm ? 'Cancel' : '+ Add IP to Whitelist'}
          </button>
          <button 
            className="btn btn-secondary" 
            onClick={fetchChangeLogs}
          >
            📋 View Change Logs
          </button>
        </div>
      </div>

      {message.text && (
        <div className={message.type === 'success' ? 'success' : 'error'} style={{ marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      {showForm && (
        <div className="card">
          <div className="card-header">
            <h2>Add IP to Whitelist</h2>
          </div>
          
          <form onSubmit={handleCreate}>
            <div className="form-group">
              <label>IP Address *</label>
              <input
                type="text"
                name="ip_address"
                className="form-control"
                placeholder="e.g., 192.168.1.100, 203.0.113.0"
                value={formData.ip_address}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group">
              <label>Description</label>
              <input
                type="text"
                name="description"
                className="form-control"
                placeholder="e.g., Office network, VPN server"
                value={formData.description}
                onChange={handleInputChange}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
              <div className="form-group">
                <label>Purpose</label>
                <input
                  type="text"
                  name="purpose"
                  className="form-control"
                  placeholder="e.g., Admin access, Development"
                  value={formData.purpose}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label>Expires At (Optional)</label>
                <input
                  type="datetime-local"
                  name="expires_at"
                  className="form-control"
                  value={formData.expires_at}
                  onChange={handleInputChange}
                />
              </div>
            </div>

            <button type="submit" className="btn btn-success">
              Add to Whitelist
            </button>
          </form>
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>IP Whitelist</h2>
        </div>

        <div className="filters">
          <div className="filter-item">
            <select
              className="form-control"
              value={filter}
              onChange={(e) => {
                setFilter(e.target.value);
                setPagination(prev => ({ ...prev, page: 1 }));
              }}
            >
              <option value="">All IPs</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Loading IP whitelist...</div>
        ) : ipList.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
            No IPs in whitelist
          </div>
        ) : (
          <>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>IP Address</th>
                    <th>Description</th>
                    <th>Purpose</th>
                    <th>Status</th>
                    <th>Expires</th>
                    <th>Added</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {ipList.map((ip) => (
                    <tr key={ip.id}>
                      <td>{ip.id}</td>
                      <td><strong>{ip.ip_address}</strong></td>
                      <td>{ip.description || '-'}</td>
                      <td>{ip.purpose || '-'}</td>
                      <td>
                        <span className={`badge ${ip.is_active ? 'badge-success' : 'badge-danger'}`}>
                          {ip.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td>{ip.expires_at ? new Date(ip.expires_at).toLocaleString() : 'Never'}</td>
                      <td>{new Date(ip.created_at).toLocaleDateString()}</td>
                      <td>
                        {ip.is_active && (
                          <button
                            className="btn btn-danger"
                            style={{ padding: '5px 10px', fontSize: '12px' }}
                            onClick={() => handleRemove(ip.id, ip.ip_address)}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
              >
                Previous
              </button>
              <span>
                Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit) || 1}
              </span>
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
              >
                Next
              </button>
            </div>
          </>
        )}
      </div>

      {/* Change Logs Modal */}
      {showChangeLogs && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '8px',
            padding: '30px',
            maxWidth: '1000px',
            maxHeight: '80vh',
            overflow: 'auto',
            width: '100%'
          }}>
            <h2>IP Whitelist Change Logs</h2>
            <p style={{ color: '#7f8c8d', marginBottom: '20px' }}>
              IP 변경 이력 (Compliance: 제8조 - 1년 보관)
            </p>

            {changeLogs.length === 0 ? (
              <p style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
                No change logs
              </p>
            ) : (
              <div className="table-container">
                <table>
                  <thead>
                    <tr>
                      <th>IP Address</th>
                      <th>Action</th>
                      <th>Description</th>
                      <th>Changed By</th>
                      <th>IP</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {changeLogs.map((log, idx) => (
                      <tr key={idx}>
                        <td><strong>{log.ip_address}</strong></td>
                        <td>
                          <span className={`badge ${
                            log.change_type === 'add' ? 'badge-success' :
                            log.change_type === 'remove' ? 'badge-danger' : 'badge-warning'
                          }`}>
                            {log.change_type}
                          </span>
                        </td>
                        <td>{log.description}</td>
                        <td>{log.changed_by}</td>
                        <td style={{ fontSize: '11px' }}>{log.changed_by_ip || '-'}</td>
                        <td>{new Date(log.created_at).toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <button 
              className="btn btn-secondary" 
              onClick={() => setShowChangeLogs(false)}
              style={{ marginTop: '20px' }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      <div className="card" style={{ background: '#fff3cd', border: '1px solid #ffc107' }}>
        <h3>🔒 IP Access Control (개인정보보호법 제6조)</h3>
        <p style={{ marginTop: '10px', fontSize: '14px', lineHeight: '1.6' }}>
          Only whitelisted IP addresses can access the backoffice system.
          All IP changes are logged for security compliance.
        </p>
        <ul style={{ marginTop: '10px', fontSize: '14px', lineHeight: '1.8' }}>
          <li>IP whitelist is enforced at the application level</li>
          <li>All IP additions and removals are logged with admin identity and timestamp</li>
          <li>Logs are retained for 1 year for audit purposes</li>
        </ul>
      </div>
    </div>
  );
}

export default IPWhitelist;